import { useState, useEffect, useMemo } from 'react';
import { format, addDays } from 'date-fns';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFinance } from '@/contexts/FinanceContext';
import { useCreateBoleto, useUpdateBoleto } from './useBoletos';
import type { BillingInvoice } from './types';

function parseBrl(s: string): number {
  const cleaned = s
    .replace(/[^\d,.-]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  edit?: BillingInvoice | undefined;
}

export function BoletoModal({ open, onOpenChange, edit }: Props) {
  const { accounts, categories, costCenters } = useFinance();
  const create = useCreateBoleto();
  const update = useUpdateBoleto();

  const incomeCategories = useMemo(
    () => categories.filter((c) => c.type === 'income'),
    [categories],
  );
  const activeAccounts = useMemo(() => accounts.filter((a) => a.isActive), [accounts]);
  const activeCostCenters = useMemo(() => costCenters.filter((cc) => cc.isActive), [costCenters]);

  const [payeeName, setPayeeName] = useState('');
  const [payeeDocument, setPayeeDocument] = useState('');
  const [payeeEmail, setPayeeEmail] = useState('');
  const [payeePhone, setPayeePhone] = useState('');
  const [description, setDescription] = useState('');
  const [amountStr, setAmountStr] = useState('');
  const [dueDate, setDueDate] = useState(format(addDays(new Date(), 7), 'yyyy-MM-dd'));
  const [accountId, setAccountId] = useState('');
  const [categoryId, setCategoryId] = useState<string>('none');
  const [costCenterId, setCostCenterId] = useState<string>('none');
  const [notes, setNotes] = useState('');
  const [barCode, setBarCode] = useState('');
  const [boletoUrl, setBoletoUrl] = useState('');

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    if (edit) {
      setPayeeName(edit.payee_name);
      setPayeeDocument(edit.payee_document ?? '');
      setPayeeEmail(edit.payee_email ?? '');
      setPayeePhone(edit.payee_phone ?? '');
      setDescription(edit.description);
      setAmountStr(Number(edit.amount).toFixed(2).replace('.', ','));
      setDueDate(edit.due_date);
      setAccountId(edit.account_id ?? '');
      setCategoryId(edit.category_id ?? 'none');
      setCostCenterId(edit.cost_center_id ?? 'none');
      setNotes(edit.notes ?? '');
      setBarCode(edit.bar_code ?? '');
      setBoletoUrl(edit.boleto_url ?? '');
    } else {
      setPayeeName('');
      setPayeeDocument('');
      setPayeeEmail('');
      setPayeePhone('');
      setDescription('');
      setAmountStr('');
      setDueDate(format(addDays(new Date(), 7), 'yyyy-MM-dd'));
      setAccountId(activeAccounts[0]?.id ?? '');
      setCategoryId('none');
      setCostCenterId('none');
      setNotes('');
      setBarCode('');
      setBoletoUrl('');
    }
  }, [open, edit, activeAccounts]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const amount = parseBrl(amountStr);
  const valid = !!payeeName.trim() && !!description.trim() && amount > 0 && !!accountId;
  const isPending = create.isPending || update.isPending;

  const handleSubmit = async () => {
    if (!valid) return;
    const payload = {
      payeeName,
      payeeDocument,
      payeeEmail,
      payeePhone,
      description,
      amount,
      dueDate,
      accountId,
      categoryId: categoryId === 'none' ? null : categoryId,
      costCenterId: costCenterId === 'none' ? null : costCenterId,
      notes,
      barCode,
      boletoUrl,
    };
    try {
      if (edit) {
        await update.mutateAsync({ id: edit.id, ...payload });
      } else {
        await create.mutateAsync(payload);
      }
      onOpenChange(false);
    } catch {
      // toast já mostrou
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-brand text-lg">
            {edit ? 'Editar boleto' : 'Novo boleto'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            <strong>Modo manual.</strong> A integração com Asaas será ativada em breve — quando ela
            entrar, código de barras, URL do PDF e atualização de status serão preenchidos
            automaticamente.
          </div>

          <div className="rounded-lg border border-border p-3">
            <div className="mb-2 text-sm font-semibold">Pagador</div>
            <div className="space-y-2">
              <Input
                placeholder="Nome do pagador *"
                value={payeeName}
                onChange={(e) => setPayeeName(e.target.value)}
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="CPF / CNPJ"
                  value={payeeDocument}
                  onChange={(e) => setPayeeDocument(e.target.value)}
                />
                <Input
                  placeholder="Telefone"
                  value={payeePhone}
                  onChange={(e) => setPayeePhone(e.target.value)}
                />
              </div>
              <Input
                placeholder="E-mail"
                type="email"
                value={payeeEmail}
                onChange={(e) => setPayeeEmail(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="boleto-desc">Descrição *</Label>
            <Input
              id="boleto-desc"
              placeholder="Ex: Mensalidade ortodontia — abril/2026"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="boleto-amount">Valor *</Label>
              <Input
                id="boleto-amount"
                placeholder="R$ 0,00"
                value={amountStr}
                onChange={(e) => setAmountStr(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="boleto-due">Vencimento *</Label>
              <Input
                id="boleto-due"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Conta de recebimento *</Label>
              <Select value={accountId} onValueChange={setAccountId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {activeAccounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Categoria</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— sem categoria —</SelectItem>
                  {incomeCategories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Centro de custo</Label>
            <Select value={costCenterId} onValueChange={setCostCenterId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— sem centro de custo —</SelectItem>
                {activeCostCenters.map((cc) => (
                  <SelectItem key={cc.id} value={cc.id}>
                    {cc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <details className="rounded-lg border border-border">
            <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-secondary/30">
              Dados do boleto (opcional)
            </summary>
            <div className="space-y-2 border-t border-border p-3">
              <div className="space-y-1.5">
                <Label htmlFor="bar-code" className="text-xs">
                  Linha digitável / código de barras
                </Label>
                <Input
                  id="bar-code"
                  placeholder="34191.79001 01043.510047 91020.150008 4 84410026000"
                  value={barCode}
                  onChange={(e) => setBarCode(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="boleto-url" className="text-xs">
                  URL do PDF
                </Label>
                <Input
                  id="boleto-url"
                  placeholder="https://..."
                  value={boletoUrl}
                  onChange={(e) => setBoletoUrl(e.target.value)}
                />
              </div>
            </div>
          </details>

          <div className="space-y-1.5">
            <Label htmlFor="boleto-notes">Observações</Label>
            <Textarea
              id="boleto-notes"
              rows={2}
              placeholder="Detalhes internos sobre a cobrança"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!valid || isPending}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {isPending ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" />
                Salvando…
              </>
            ) : edit ? (
              'Salvar alterações'
            ) : (
              'Criar boleto'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
